import type { FormikErrors } from 'formik';

/**
 * FormikErrors cannot just be "dumped into JSX", because technically they can be nested objects or arrays of string.
 * This function joins arrays, and passes through strings, but it will not deal with nested objects.
 * If it encounters nested objects, it will return a generic error message and also log an error in development.
 *
 * This is still a better scenario than ending up with "[object Object]" in the UI, which is what happens if you just
 * pass a FormikError into JSX.
 *
 * Long-term we should migrate to a form library that types errors more predictable, but for now this is a good stopgap.
 */
export function ensureFormikErrorIsString(
  error:
    | string
    | string[]
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    | FormikErrors<any>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    | FormikErrors<any>[]
    | undefined,
): string | undefined {
  if (typeof error === 'string') {
    return error;
  }
  if (Array.isArray(error)) {
    return error.map(ensureFormikErrorIsString).join(', ');
  }
  if (error == null) {
    return undefined;
  }
  if (process.env.NODE_ENV === 'development') {
    // eslint-disable-next-line no-console
    console.error(
      'Accessing a non-leaf FormikError via `ensureFormikErrorIsString`',
    );
  }
  return 'An unexpected error occurred. Please check your input.';
}
